import AbstractShape, {Dimensions} from "./AbstractShape";
import Length from "./../../../../source/js/shared/models/Geometry/Length";
import EnclosedShape from "./../EnclosedShape";

import makerjs from "makerjs";
import {Font} from "opentype.js";
import computeLayout from "opentype-layout";

import mapValues from "lodash/mapValues";
import trim from "lodash/trim";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";
import DimensionLine from "../Lines/DimensionLine";
import GuideLine from "../Lines/GuideLine";

export class Text extends AbstractShape {
	private readonly fonts: object;

	constructor( dimensions?: object, fonts?: object, modifiers?: object ) {
		super( 'text', dimensions, modifiers );

		this.fonts = fonts;
	}

	createModel(): void {
		const primitives = mapValues( this.cleanedPrimitives, ( primitive ) => primitive instanceof Length ? primitive.in_cm() : primitive );

		if ( ! this.fonts || ! primitives.text || ! primitives.desired_length ) {
			return;
		}

		const model = this.createTextModel( this.fonts, primitives );

		if ( isEmpty( model ) ) {
			return;
		}

		this.model = this.calculateLetterEnclosing( model );
		this.model.type = 'text';
	}

	createTextModel( fonts, { text, font, desired_dimension: desiredDimension, desired_length: desiredLength } ): MakerJs.models.Text {
		if ( ! fonts || isEmpty( fonts ) || isUndefined( font ) ) {
			return;
		}

		const fontObject = fonts.find( fontObj => fontObj.fontName === font );
		const ratio = this.calculateRatio( fontObject.font, text, desiredLength, desiredDimension );
		const fontSize = Length.from_cm( desiredLength / ratio );

		if ( desiredDimension === 'height' ) {
			return this.createFixedHeightText( fontObject.font, text, fontSize );
		}

		return new makerjs.models.Text(	fontObject.font, trim( text ), fontSize.in_mm() );
	}

	protected calculateRatio( font: Font, text: string, desiredLength: number, desiredDimension: string ): number {
		const firstRender = new makerjs.models.Text( font, text, Length.from_cm( desiredLength ).in_mm() );
		const enclosingRectangle = makerjs.measure.modelExtents( firstRender );

		const actualLength = desiredDimension === 'height' ?
			Length.from_cm( enclosingRectangle.height ).in_cm() :
			Length.from_cm( enclosingRectangle.width ).in_cm();

		return actualLength / desiredLength;
	}

	/**
	 * Wraps longer texts to ensure it fits on the desired sheet.
	 * @see https://app.asana.com/0/0/1203855198149975/f
	 *
	 * @param {Font} font The font to use.
	 * @param {string} text The text to render.
	 * @param {Length} fontSize The desired size/height of the font.
	 */
	createFixedHeightText( font: Font, text: string, fontSize: Length ): MakerJs.models.Text {
		const { shapeSettings } = window.measurementSelectorVariables;
		const textModel = { models: {} };
		const scale = 1 / font.unitsPerEm * fontSize.in_mm();

		const layout = computeLayout( font, trim( text ), {
			align: 'left',
			lineHeight: font.unitsPerEm,
			width: new Length( shapeSettings.maximumLength ).in_cm() / scale,
		} );

		layout.glyphs.forEach( ( glyph, i ) => {
			const character = makerjs.models.Text.glyphToModel( glyph.data, fontSize.in_mm() );
			character.origin = makerjs.point.scale( glyph.position, scale );

			makerjs.model.addModel( textModel, character, i );
		} );
		makerjs.model.zero( textModel );
		makerjs.model.originate( textModel );

		return textModel;
	}

	getHeight(): Length {
		return this.getLength();
	}

	getLength(): Length {
		return this.getEnclosingRectangle().high()[ 1 ].subtract( this.getEnclosingRectangle().low()[ 1 ] ).round();
	}

	getWidth(): Length {
		return this.getEnclosingRectangle().high()[ 0 ].subtract( this.getEnclosingRectangle().low()[ 0 ] ).round();
	}

	getDesiredLength(): Length {
		return this.cleanedPrimitives.desired_length;
	}

	getDimensionLines(ratio = 1, focusedElement = '', forceRender = false): DimensionLine[] {
		if ( ! this.model ) {
			return [];
		}

		return [
			new DimensionLine( [0, 0], [ 0, this.getLength().in_mm() ], this.renderDimensionLabel( 'height', this.getLength().in_cm() ), [-40, 0], ratio ), // Vertical line
			new DimensionLine( [0, 0], [ this.getWidth().in_mm(), 0 ], this.renderDimensionLabel( 'width', this.getWidth().in_cm() ), [0, -40], ratio ), // Horizontal line
		];
	}

	getGuideLines(): GuideLine[] {
		const model = makerjs.model.clone(this.model);
		makerjs.model.scale(model, 10, true); // Convert to mm
		const measure = makerjs.measure.modelExtents(model);

		// Add top and bottom guidelines
		if(measure) {
			return [
				new GuideLine(
					[measure.low[0], measure.high[1]],
					[measure.high[0], measure.high[1]]
				),
				new GuideLine(
					[measure.low[0], measure.low[1]],
					[measure.high[0], measure.low[1]]
				),
			];
		}

		return [];
	}

	calculateLetterEnclosing( textModel: MakerJs.models.Text ): MakerJs.models.Text {
		for ( const [, model] of Object.entries( textModel.models ) ) {
			model[ 'enclosed' ] = new EnclosedShape( model );
		}

		return textModel;
	}

	getDesiredDimension(): string {
		return this.cleanedPrimitives.desired_dimension;
	}

	getDefaultDimensions(): Dimensions {
		return { desired_length: 20 };
	}

	getDefaultCutoutDimensions(parentShape: AbstractShape): Dimensions {

		// The default cutout is based on the width so we can properly scale based on the parent shape.
		return { desired_length: parentShape.getWidth().multiply( 0.8 ).in_cm() };
	}

	getDefaultCutoutModifiers(): object {
		const defaultModifiers = this.getDefaultModifiers();

		return { ...defaultModifiers, desired_dimension: 'width' };
	}

	getDefaultModifiers(): object {
		const { loggedInUser, i18n } = window.measurementSelectorVariables;
		const defaultText = ! isEmpty( loggedInUser ) ? `${i18n.text.default} ${loggedInUser}`: i18n.text.default;

		return {
			font: 'Exo-Bold',
			text: defaultText,
			desired_dimension: 'height',
		};
	}
}
